import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, ChevronDown } from 'lucide-react'
import { clsx } from 'clsx'
import { trackNavigation, trackConsultationRequest } from '@lib/analytics'

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isServicesOpen, setIsServicesOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const mobileMenuRef = useRef(null)
  const servicesRef = useRef(null)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsMobileMenuOpen(false)
    setIsServicesOpen(false)
  }, [location])

  // Handle click outside to close mobile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false)
      }
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setIsServicesOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const navigationItems = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { 
      name: 'Services', 
      href: '/services',
      hasSubmenu: true,
      submenu: [
        { name: 'Equity Compensation & LTIs', href: '/services', hash: 'equity' },
        { name: 'Executive Compensation & IPO', href: '/services', hash: 'executive' },
        { name: 'Variable Pay & Sales Comp', href: '/services', hash: 'variable' },
        { name: 'Comprehensive Benefits', href: '/services', hash: 'benefits' },
        { name: 'Compensation Consulting', href: '/services', hash: 'benchmarking' },
        { name: 'Total Rewards & Performance', href: '/services', hash: 'rewards' }
      ]
    },
    { name: 'Resources', href: '/resources' },
    { name: 'Certification', href: '/certification' },
    { name: 'Careers', href: '/careers' },
    { name: 'Contact', href: '/contact' }
  ]

  const handleSubmenuClick = (href, hash) => {
    const destination = hash ? `${href}#${hash}` : href
    trackNavigation(destination, 'submenu')
    
    setIsServicesOpen(false)
    setIsMobileMenuOpen(false)
    
    if (location.pathname === href) {
      // If already on the page, just scroll to the section
      setTimeout(() => {
        const element = document.getElementById(hash)
        if (element) {
          const navbarHeight = 56 // Account for reduced navbar height
          const elementPosition = element.offsetTop - navbarHeight
          window.scrollTo({ 
            top: elementPosition, 
            behavior: 'smooth' 
          })
        }
      }, 200) // Increased delay for better coordination
    } else {
      // Navigate to page first, then scroll
      navigate(`${href}#${hash}`)
    }
  }

  const handleNavClick = (href, source = 'nav') => {
    trackNavigation(href, source)
  }

  const handleConsultationClick = (source = 'nav') => {
    trackConsultationRequest(source)
  }

  const isActivePath = (path) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={clsx(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300 print:hidden',
        isScrolled
          ? 'bg-black/95 backdrop-blur-md shadow-elegant border-b border-neutral-800/50'
          : 'bg-black/90 backdrop-blur-sm'
      )}
      ref={mobileMenuRef}
    >
      <div className="w-full px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-12 lg:h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative">
              <img 
                src="/TRP_logo_compressed.png" 
                alt="Total Rewards Pro Logo" 
                className="h-8 lg:h-10 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="block">
              <h1 className="text-sm sm:text-base lg:text-lg font-open-sans font-semibold text-white group-hover:text-neutral-200 transition-colors">
                Total Rewards Pro
              </h1>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navigationItems.map((item) => (
              <div key={item.name} className="relative">
                {item.hasSubmenu ? (
                  <div
                    className="relative"
                    ref={servicesRef}
                    onMouseEnter={() => setIsServicesOpen(true)}
                    onMouseLeave={() => setIsServicesOpen(false)}
                  >
                    <Link
                      to={item.href}
                      className={clsx(
                        'nav-link text-sm font-medium transition-all duration-200 flex items-center space-x-1',
                        isActivePath(item.href)
                          ? 'text-white after:w-full'
                          : 'text-neutral-300 hover:text-white'
                      )}
                    >
                      <span>{item.name}</span>
                      <ChevronDown className={clsx(
                        'w-4 h-4 transition-transform duration-200',
                        isServicesOpen ? 'rotate-180' : ''
                      )} />
                    </Link>
                    
                    <AnimatePresence>
                      {isServicesOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 mt-2 w-64 bg-white/95 backdrop-blur-md border border-neutral-200/50 rounded-xl shadow-elegant py-2"
                        >
                          {item.submenu.map((subItem) => (
                            <button
                              key={subItem.name}
                              onClick={() => handleSubmenuClick(subItem.href, subItem.hash)}
                              className="block w-full text-left px-4 py-3 text-sm text-neutral-600 hover:text-navy-950 hover:bg-neutral-50 transition-colors"
                            >
                              {subItem.name}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={clsx(
                      'nav-link text-sm font-medium transition-all duration-200',
                      isActivePath(item.href)
                        ? 'text-white after:w-full'
                        : 'text-neutral-300 hover:text-white'
                    )}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
            
            {/* CTA Button */}
            <Link
              to="/contact#consultation"
              className="btn-primary ml-4"
              onClick={() => handleConsultationClick('nav-desktop')}
            >
              Schedule Consultation
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-neutral-300 hover:text-white hover:bg-neutral-800 transition-all duration-200 focus-ring"
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden border-t border-neutral-800/50 bg-black/95 backdrop-blur-md"
            >
              <div className="py-6 space-y-3 px-4">
                {navigationItems.map((item) => (
                  <div key={item.name}>
                    <Link
                      to={item.href}
                      className={clsx(
                        'block px-4 py-4 text-base font-medium rounded-xl transition-all duration-200 min-h-[48px] flex items-center',
                        isActivePath(item.href)
                          ? 'text-white bg-primary-500/20 border border-primary-500/30'
                        : 'text-neutral-300 hover:text-white hover:bg-neutral-800'
                      )}
                    >
                      {item.name}
                    </Link>
                    {item.hasSubmenu && (
                      <div className="ml-4 mt-3 space-y-2">
                        {item.submenu.map((subItem) => (
                          <button
                            key={subItem.name}
                            onClick={() => handleSubmenuClick(subItem.href, subItem.hash)}
                            className="block w-full text-left px-4 py-3 text-sm text-neutral-400 hover:text-white hover:bg-neutral-800 rounded-lg transition-colors min-h-[44px] flex items-center"
                          >
                            {subItem.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                
                <div className="pt-6 border-t border-neutral-800/50 mt-6">
                  <Link
                    to="/contact#consultation"
                    className="btn-primary w-full justify-center min-h-[48px] flex items-center"
                    onClick={() => handleConsultationClick('nav-mobile')}
                  >
                    Schedule Consultation
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  )
}

export default Navigation 